import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Search, Filter, Home, Dog, Baby, CheckCircle, XCircle, 
  Eye, EyeOff, RotateCcw, Ban, Clock, AlertTriangle,
  ChevronDown, Loader2, ExternalLink, User, Calendar,
  MoreVertical
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import PanelLayout from '@/components/PanelLayout';

import { API } from '@/utils/api';
import { getImageUrl } from '@/utils/imageUrl';
import { useTranslation } from 'react-i18next';

// Status badge component
function StatusBadge({ status }) {
  const styles = {
    pending: "bg-yellow-100 text-yellow-700 border-yellow-200",
    pending_verification: "bg-purple-100 text-purple-700 border-purple-200",
    published: "bg-green-100 text-green-700 border-green-200",
    hidden: "bg-zinc-100 text-zinc-600 border-zinc-200",
    rejected: "bg-red-100 text-red-700 border-red-200"
  };
  
  const labels = {
    pending: "⏳ Oczekuje",
    pending_verification: "🏆 Weryfikacja tytułów",
    published: "✅ Opublikowane",
    hidden: "👁 Ukryte",
    rejected: "❌ Odrzucone"
  };

  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full border ${styles[status] || styles.pending}`}>
      {labels[status] || status}
    </span>
  );
}

// Type icon component
function TypeIcon({ type }) {
  const icons = {
    kennel: <Home className="w-4 h-4" />,
    stud: <Dog className="w-4 h-4" />,
    litter: <Baby className="w-4 h-4" />
  };
  
  const colors = {
    kennel: "bg-blue-100 text-blue-600",
    stud: "bg-purple-100 text-purple-600",
    litter: "bg-pink-100 text-pink-600"
  };

  return (
    <div className={`w-8 h-8 rounded-lg ${colors[type]} flex items-center justify-center`}>
      {icons[type]}
    </div>
  );
}

// Moderation Item Row
function ModerationItem({ item, onAction }) {
  const [showActions, setShowActions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState('');
  const [showReasonInput, setShowReasonInput] = useState(false);

  const handleAction = async (action) => {
    if ((action === 'reject' || action === 'hide') && !reason && !showReasonInput) {
      setShowReasonInput(true);
      return;
    }
    
    setLoading(true);
    try {
      await onAction(item.type, item.id, action, reason);
      setShowActions(false);
      setShowReasonInput(false);
      setReason('');
    } finally {
      setLoading(false);
    }
  };

  const publicUrls = {
    kennel: `/hodowla/${item.slug}`,
    stud: `/reproduktor/${item.slug}`,
    litter: `/miot/${item.slug}`
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start gap-4">
        {/* Thumbnail */}
        <div className="flex-shrink-0">
          {item.thumbnail ? (
            <img 
              src={getImageUrl(item.thumbnail)}
              alt={item.name}
              className="w-16 h-16 rounded-lg object-cover bg-zinc-100"
            />
          ) : (
            <div className="w-16 h-16 rounded-lg bg-zinc-100 flex items-center justify-center">
              <TypeIcon type={item.type} />
            </div>
          )}
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <TypeIcon type={item.type} />
                <h3 className="font-semibold text-zinc-900 truncate">{item.name}</h3>
                {item.flagged && (
                  <AlertTriangle className="w-4 h-4 text-red-500" title="Podejrzana treść" />
                )}
              </div>
              <div className="flex items-center gap-3 text-sm text-zinc-500">
                <span className="flex items-center gap-1">
                  <User className="w-3.5 h-3.5" />
                  {item.user_email}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5" />
                  {new Date(item.created_at).toLocaleDateString('pl-PL')}
                </span>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <StatusBadge status={item.public_status} />
              
              {/* Completeness */}
              <div className="hidden sm:flex items-center gap-1" title="Kompletność profilu">
                <div className="w-12 h-2 bg-zinc-200 rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full ${
                      item.completeness >= 80 ? 'bg-green-500' : 
                      item.completeness >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${item.completeness}%` }}
                  />
                </div>
                <span className="text-xs text-zinc-500">{item.completeness}%</span>
              </div>
            </div>
          </div>
          
          {/* Flag reasons */}
          {item.flag_reasons && item.flag_reasons.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {item.flag_reasons.map((r, i) => (
                <span key={i} className="px-2 py-0.5 bg-red-50 text-red-600 text-xs rounded">
                  {r}
                </span>
              ))}
            </div>
          )}

          {/* Show titles for verification */}
          {item.public_status === 'pending_verification' && item.selected_championships && (
            <div className="mt-2 p-2 bg-purple-50 rounded-lg border border-purple-100">
              <p className="text-xs font-medium text-purple-700 mb-1">🏆 Tytuły do weryfikacji:</p>
              <div className="flex flex-wrap gap-1">
                {item.selected_championships?.map((t, i) => (
                  <span key={i} className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded">
                    {t}
                  </span>
                ))}
                {item.selected_show_achievements?.map((t, i) => (
                  <span key={`show-${i}`} className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded">
                    {t}
                  </span>
                ))}
                {item.selected_working_achievements?.map((t, i) => (
                  <span key={`work-${i}`} className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                    {t}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {/* Moderation reason */}
          {item.moderation_reason && (
            <p className="mt-2 text-sm text-zinc-500 italic">
              Powód: {item.moderation_reason}
            </p>
          )}
        </div>
      </div>
      
      {/* Actions */}
      <div className="mt-4 flex items-center justify-between border-t border-gray-100 pt-3">
        <a 
          href={publicUrls[item.type]} 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-sm text-[#F47A3B] hover:underline flex items-center gap-1"
        >
          <Eye className="w-4 h-4" />
          Podgląd
          <ExternalLink className="w-3 h-3" />
        </a>
        
        <div className="flex items-center gap-2">
          {showReasonInput ? (
            <div className="flex items-center gap-2">
              <Input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Powód odrzucenia..."
                className="w-48 h-8 text-sm"
              />
              <Button
                size="sm"
                onClick={() => handleAction('reject')}
                disabled={!reason || loading}
                className="bg-red-500 hover:bg-red-600 text-white h-8"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Odrzuć'}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => { setShowReasonInput(false); setReason(''); }}
                className="h-8"
              >
                Anuluj
              </Button>
            </div>
          ) : (
            <>
              {/* Verify Titles button for pending_verification */}
              {item.public_status === 'pending_verification' && (
                <Button
                  size="sm"
                  onClick={() => handleAction('verify_titles')}
                  disabled={loading}
                  className="bg-purple-500 hover:bg-purple-600 text-white h-8"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                    <>
                      🏆 Zweryfikuj tytuły
                    </>
                  )}
                </Button>
              )}

              {item.public_status !== 'published' && item.public_status !== 'pending_verification' && (
                <Button
                  size="sm"
                  onClick={() => handleAction('publish')}
                  disabled={loading}
                  className="bg-green-500 hover:bg-green-600 text-white h-8"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Publikuj
                    </>
                  )}
                </Button>
              )}
              
              {item.public_status === 'published' && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleAction('hide')}
                  disabled={loading}
                  className="h-8"
                >
                  <EyeOff className="w-4 h-4 mr-1" />
                  Ukryj
                </Button>
              )}
              
              {item.public_status !== 'rejected' && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowReasonInput(true)}
                  disabled={loading}
                  className="h-8 text-red-600 border-red-200 hover:bg-red-50"
                >
                  <XCircle className="w-4 h-4 mr-1" />
                  Odrzuć
                </Button>
              )}
              
              {(item.public_status === 'hidden' || item.public_status === 'rejected') && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleAction('restore')}
                  disabled={loading}
                  className="h-8"
                >
                  <RotateCcw className="w-4 h-4 mr-1" />
                  Przywróć
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminModeration() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  
  // Filters from URL
  const status = searchParams.get('status') || 'pending';
  const type = searchParams.get('type') || '';
  const flagged = searchParams.get('flagged') === 'true';

  useEffect(() => {
    fetchItems();
  }, [status, type, flagged]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (status) params.append('status', status);
      if (type) params.append('type', type);
      if (flagged) params.append('flagged', 'true');
      if (search) params.append('search', search);
      
      const res = await axios.get(`${API}/admin/moderation?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setItems(res.data.items || []);
    } catch (e) {
      console.error('Error fetching moderation queue:', e);
      toast.error('Błąd ładowania danych');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (entityType, entityId, action, reason) => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ action });
      if (reason) params.append('reason', reason);
      
      await axios.put(
        `${API}/admin/moderation/${entityType}/${entityId}?${params.toString()}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Akcja "${action}" wykonana pomyślnie`);
      fetchItems(); // Refresh list
    } catch (e) {
      console.error('Error performing action:', e);
      toast.error('Błąd wykonywania akcji');
    }
  };

  const setFilter = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    setSearchParams(newParams);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchItems();
  };

  return (
    <PanelLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-zinc-900">Kolejka moderacji</h1>
            <p className="text-zinc-500">Weryfikuj i zarządzaj stronami użytkowników</p>
          </div>
          
          <Link to="/admin/dashboard">
            <Button variant="outline">
              ← Dashboard
            </Button>
          </Link>
        </div>

        {/* Filters */}
        <div className="bg-white border border-zinc-200 rounded-xl p-4 space-y-4">
          {/* Search */}
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Szukaj po nazwie, opisie..."
                className="pl-9"
              />
            </div>
            <Button type="submit" variant="outline">Szukaj</Button>
          </form>
          
          {/* Filter chips */}
          <div className="flex flex-wrap gap-2">
            <span className="text-sm text-zinc-500 mr-2">Status:</span>
            {['pending', 'pending_verification', 'published', 'hidden', 'rejected', 'all'].map(s => (
              <button
                key={s}
                onClick={() => setFilter('status', s === 'all' ? '' : s)}
                className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                  (status === s || (!status && s === 'all'))
                    ? 'bg-[#F47A3B] text-white border-[#F47A3B]'
                    : 'bg-white text-zinc-600 border-zinc-200 hover:border-zinc-300'
                }`}
              >
                {s === 'pending' ? '⏳ Oczekujące' :
                 s === 'pending_verification' ? '🏆 Weryfikacja tytułów' :
                 s === 'published' ? '✅ Opublikowane' :
                 s === 'hidden' ? '👁 Ukryte' :
                 s === 'rejected' ? '❌ Odrzucone' : '📋 Wszystkie'}
              </button>
            ))}
          </div>
          
          <div className="flex flex-wrap gap-2">
            <span className="text-sm text-zinc-500 mr-2">Typ:</span>
            {['', 'kennel', 'stud', 'litter'].map(t => (
              <button
                key={t}
                onClick={() => setFilter('type', t)}
                className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                  type === t
                    ? 'bg-blue-500 text-white border-blue-500'
                    : 'bg-white text-zinc-600 border-zinc-200 hover:border-zinc-300'
                }`}
              >
                {t === '' ? '🔹 Wszystkie' :
                 t === 'kennel' ? '🏠 Hodowle' :
                 t === 'stud' ? '🐕 Reproduktory' : '🐶 Mioty'}
              </button>
            ))}
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setFilter('flagged', flagged ? '' : 'true')}
              className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                flagged
                  ? 'bg-red-500 text-white border-red-500'
                  : 'bg-white text-zinc-600 border-zinc-200 hover:border-zinc-300'
              }`}
            >
              <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
              Tylko podejrzane
            </button>
          </div>
        </div>

        {/* Items List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-[#F47A3B] animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="bg-white border border-zinc-200 rounded-xl p-12 text-center">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-zinc-900 mb-2">Brak elementów</h3>
            <p className="text-zinc-500">
              {status === 'pending' 
                ? 'Wszystko sprawdzone! Brak stron oczekujących na weryfikację.'
                : 'Brak elementów pasujących do wybranych filtrów.'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-zinc-500">
              Znaleziono: <span className="font-medium">{items.length}</span> elementów
            </p>
            {items.map(item => (
              <ModerationItem 
                key={`${item.type}-${item.id}`} 
                item={item} 
                onAction={handleAction}
              />
            ))}
          </div>
        )}
      </div>
    </PanelLayout>
  );
}
